import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { uiTokens } from './tokens';

type SearchBarSize = 'md' | 'lg';
type SearchBarVariant = 'glass' | 'hero' | 'light' | 'permit';

interface SearchBarProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  placeholder?: string;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  submitIcon?: React.ReactNode;
  size?: SearchBarSize;
  variant?: SearchBarVariant;
  disabled?: boolean;
  className?: string;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  minRows?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  maxHeight?: number;
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  style?: React.CSSProperties;
  onHeightChange?: (nextHeight: number, scrollHeight: number) => void;
}

const Shell = styled.div<{ $size: SearchBarSize; $variant: SearchBarVariant }>`
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: ${uiTokens.radius.pill};
  padding: ${({ $size, $variant }) =>
    $variant === 'permit'
      ? '10px 10px 10px 22px'
      : ($size === 'lg' ? '12px 14px 12px 18px' : '10px 12px 10px 14px')};
  background: ${({ $variant }) =>
    $variant === 'light'
      ? '#f5f5f5'
      : ($variant === 'permit' ? 'rgba(16, 31, 35, 0.74)' : ($variant === 'hero' ? uiTokens.colors.bgGlass : uiTokens.colors.bgElevated))};
  border: 1px solid ${({ $variant }) =>
    $variant === 'light'
      ? 'rgba(255, 255, 255, 0.75)'
      : ($variant === 'permit' ? 'rgba(115, 145, 151, 0.2)' : ($variant === 'hero' ? uiTokens.colors.borderSoft : uiTokens.colors.borderMuted))};
  box-shadow: ${({ $variant }) => ($variant === 'light' ? 'none' : ($variant === 'permit' ? '0 8px 22px rgba(0, 0, 0, 0.28)' : uiTokens.shadow.md))};
  backdrop-filter: ${({ $variant }) => ($variant === 'light' ? 'none' : 'blur(10px)')};
`;

const Slot = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 8px;
`;

const InlineTextarea = styled.textarea<{ $variant: SearchBarVariant; $maxHeight: number }>`
  flex: 1;
  min-height: 34px;
  max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
  padding: 2px 0;
  margin: 0;
  border: 0;
  border-radius: 0;
  outline: none;
  resize: none;
  box-shadow: none;
  background: transparent;
  color: ${({ $variant }) => ($variant === 'light' ? '#2f2f2f' : '#ffffff')} !important;
  -webkit-text-fill-color: ${({ $variant }) => ($variant === 'light' ? '#2f2f2f' : '#ffffff')};
  font-family: 'DM Sans', -apple-system, sans-serif;
  font-size: 18px;
  line-height: ${({ $variant }) => ($variant === 'light' ? 2.3 : 1.3)};
  display: block;
  overflow-y: hidden;

  &::placeholder {
    color: ${({ $variant }) => ($variant === 'light' ? '#5f5f5f' : ($variant === 'permit' ? 'rgba(225, 231, 242, 0.72)' : '#546170'))} !important;
    -webkit-text-fill-color: ${({ $variant }) => ($variant === 'light' ? '#5f5f5f' : ($variant === 'permit' ? 'rgba(225, 231, 242, 0.72)' : '#546170'))};
    opacity: 1;
  }

  &:focus {
    border: 0;
    outline: none;
    box-shadow: none;
    background: transparent;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button<{ $variant: SearchBarVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $variant }) => ($variant === 'permit' ? '42px' : '44px')};
  height: ${({ $variant }) => ($variant === 'permit' ? '42px' : '44px')};
  border: none;
  border-radius: ${({ $variant }) => (($variant === 'light' || $variant === 'permit') ? '999px' : '12px')};
  margin-left: 8px;
  cursor: pointer;
  color: ${({ $variant }) =>
    $variant === 'light'
      ? '#6b7280'
      : ($variant === 'permit' ? '#0a1412' : ($variant === 'hero' ? uiTokens.colors.accentTextDark : uiTokens.colors.textPrimary))};
  background: ${({ $variant }) =>
    $variant === 'light'
      ? 'transparent'
      : ($variant === 'permit' ? '#36e1b0' : ($variant === 'hero' ? uiTokens.colors.accent : 'rgba(52, 211, 153, 0.2)'))};
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === 'light'
        ? '#dfdfdf'
        : ($variant === 'permit' ? '#61eccc' : ($variant === 'hero' ? uiTokens.colors.accentHover : 'rgba(52, 211, 153, 0.3)'))};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  leftSlot,
  rightSlot,
  submitIcon,
  size = 'lg',
  variant = 'hero',
  disabled = false,
  className,
  inputRef,
  minRows = 1,
  onKeyDown,
  onFocus,
  onClick,
  autoFocus = false,
  maxHeight = 560,
  onDragOver,
  onDragLeave,
  onDrop,
  style,
  onHeightChange,
}) => {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  const setTextareaRef = (node: HTMLTextAreaElement | null) => {
    internalRef.current = node;
    if (!inputRef) {
      return;
    }
    if (typeof inputRef === 'function') {
      inputRef(node);
    } else {
      (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    }
  };

  const adjustHeight = useCallback(() => {
    const textArea = internalRef.current;
    if (!textArea) {
      return;
    }
    textArea.style.height = '34px';
    const nextHeight = Math.min(textArea.scrollHeight, maxHeight);
    textArea.style.height = `${nextHeight}px`;
    textArea.style.overflowY = textArea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    onHeightChange?.(nextHeight, textArea.scrollHeight);
  }, [maxHeight, onHeightChange]);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  return (
    <Shell
      className={className}
      $size={size}
      $variant={variant}
      style={style}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {leftSlot ? <Slot>{leftSlot}</Slot> : null}
      <InlineTextarea
        $variant={variant}
        $maxHeight={maxHeight}
        ref={setTextareaRef}
        value={value}
        onChange={(event) => {
          onChange(event);
          adjustHeight();
        }}
        placeholder={placeholder}
        rows={minRows}
        disabled={disabled}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onClick={onClick}
        autoFocus={autoFocus}
      />
      {rightSlot}
      <SubmitButton type="button" onClick={onSubmit} disabled={disabled} $variant={variant} aria-label="Send message">
        {submitIcon}
      </SubmitButton>
    </Shell>
  );
};

export default SearchBar;
